import { getBasemap, type MapEngine } from '@mighty-map/map-engine';
import type { GlobeEngine } from '@mighty-map/map-engine/cesium';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toEngineLayers } from '../../lib/engineLayers';
import { useWithAddedLayers } from '../data-passport/addedLayers';
import { GlobeView } from '../globe/GlobeView';
import { globeCameraFor, registerViewEngine } from '../globe/viewSync';
import { hiddenTargets } from '../../lib/layers-pro/runtime';
import { runtimeLayersOf, useRuntimeLayers } from '../layers/runtimeLayers';
import { useLayersPro } from '../layers/store';
import { getDataToken } from '../../state/dataToken';
import { selectActiveTheme, useMapStore } from '../../state/mapStore';
import { useViewMode } from '../../state/viewMode';

const BimView = lazy(() => import('../bim/BimView'));

/** Put the viewer's layer choices (transparency, labels) on an engine that is taking over the screen. */
function applyLayerChoices(engine: MapEngine): void {
  const { opacity, labelsOff } = useLayersPro.getState();
  for (const [id, value] of Object.entries(opacity)) engine.setOpacity(id, value);
  for (const id of useMapStore.getState().layers.keys()) engine.setLabelsVisible(id, !labelsOff[id]);
}

/**
 * The stage's three views of the same place, switched by Map · BIM · Globe. Map's engine is made at once (its
 * renderer loads in its own chunk while the shell paints); Globe and BIM are made the first time they are entered
 * and kept after, the views not on screen hidden rather than unmounted, as production does (a remount is slow).
 * The shell's tools get the engine of the view on screen (BIM keeps the one beneath it), theme switches and
 * toggles reconcile layers on both engines, and the camera follows each switch: Map lands flat, Globe keeps its
 * tilt.
 */
export function MapView({ onEngine }: { onEngine: (engine: MapEngine | null) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const [mapEngine, setMapEngine] = useState<MapEngine | null>(null);
  const [globeEngine, setGlobeEngine] = useState<GlobeEngine | null>(null);
  const mode = useViewMode((s) => s.mode);
  const shared = useViewMode((s) => s.camera);
  const [surface, setSurface] = useState<'map' | 'globe'>('map');
  const [globeMade, setGlobeMade] = useState(false);
  const [bimMade, setBimMade] = useState(false);
  const theme = useMapStore(selectActiveTheme);
  const layers = useMapStore((s) => s.layers);
  const manifest = useMapStore((s) => s.manifest);
  const visibility = useMapStore((s) => s.visibility);
  const basemapId = useMapStore((s) => s.basemapId);

  useEffect(() => {
    let cancelled = false;
    let created: MapEngine | null = null;
    void import('@mighty-map/map-engine/maplibre').then(({ createMapLibreEngine }) => {
      const el = container.current;
      const s = useMapStore.getState();
      const dataUrl = s.config?.project.data_api_url;
      if (cancelled || !el || !dataUrl) return;
      const t = selectActiveTheme(s);
      created = createMapLibreEngine(el, {
        getToken: getDataToken,
        dataUrl,
        basemapId: s.basemapId,
        ...(t ? { camera: { ...t.camera } } : {}),
        onBasemapFallback: (fromId, toId) => {
          const store = useMapStore.getState();
          store.setBasemap(toId);
          store.setNotice(
            `${getBasemap(fromId).title} can't be reached from this network; showing ${getBasemap(toId).title}.`,
          );
        },
      });
      created.onCameraChange((c) => {
        if (useViewMode.getState().mode === 'map') useMapStore.getState().setZoom(c.zoom);
      });
      const engineForClicks = created;
      created.onClick((e) => {
        // While a tool is drawing (Measure, Sketch), clicks are its vertices, not identify (as in production).
        if (engineForClicks.draw.isActive()) return;
        const top = e.features[0];
        useMapStore
          .getState()
          .setPopup(top ? { layerId: top.layerId, properties: top.properties, point: e.point } : null);
      });
      registerViewEngine('map', created);
      // A development handle for looking at the live engine from the console or a screenshot script.
      if (import.meta.env.DEV) (window as { __mmMap?: MapEngine }).__mmMap = created;
      setMapEngine(created);
    });
    const onResize = (): void => created?.resize();
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      registerViewEngine('map', null);
      created?.destroy();
    };
  }, []);

  const onGlobe = useCallback((engine: GlobeEngine | null) => setGlobeEngine(engine), []);

  // A view is made the first time it is entered; BIM keeps whichever of Map and Globe was on screen before it.
  useEffect(() => {
    if (mode === 'globe') setGlobeMade(true);
    if (mode === 'bim') setBimMade(true);
    if (mode !== 'bim') setSurface(mode);
    useMapStore.getState().setPopup(null);
  }, [mode]);

  // The shell's tools follow the view on screen. The view left behind ends its tool session (a half-drawn sketch
  // stays with the view it was drawn on) and the one taking over gets the viewer's layer choices.
  const active = surface === 'globe' ? globeEngine : mapEngine;
  const previous = useRef<MapEngine | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = active;
    if (before && before !== active) before.draw.cancel();
    if (active) applyLayerChoices(active);
    onEngine(active);
  }, [active, onEngine]);
  useEffect(() => () => onEngine(null), [onEngine]);
  // BIM keeps the engine beneath, so coming back from it is no engine change; layer choices made while BIM was on
  // screen (a Snap restored there) are put on the engine then.
  useEffect(() => {
    if (mode !== 'bim' && active) applyLayerChoices(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on leaving BIM only; an engine change is handled above
  }, [mode]);

  // The camera handed over by the view being left. Map lands flat; Globe takes a tilted Map's lean, else its own
  // tilt, or the theme's first Globe view before it has been open (globeCameraFor). Applied again if the view being
  // left writes its camera after the switch (BIM does).
  useEffect(() => {
    if (!shared) return;
    if (mode === 'map') {
      mapEngine?.jumpTo({ center: shared.center, zoom: shared.zoom, bearing: shared.bearing, pitch: 0 });
    } else if (mode === 'globe') {
      globeEngine?.jumpTo(globeCameraFor(shared, selectActiveTheme(useMapStore.getState())?.camera.globe));
    }
  }, [mode, shared, mapEngine, globeEngine]);

  const withAdded = useWithAddedLayers(
    useMemo(
      () => (theme ? toEngineLayers({ theme, layers, manifest, visibility }) : []),
      [theme, layers, manifest, visibility],
    ),
  );
  // A layer from another theme switched off in Layers Pro (its runtime block) is held back from the map.
  const rtSources = useRuntimeLayers((s) => s.bySource);
  const rtHidden = useRuntimeLayers((s) => s.hidden);
  const engineLayers = useMemo(() => {
    const off = hiddenTargets(runtimeLayersOf({ bySource: rtSources, drawn: {} }), rtHidden).layers;
    return off.size ? withAdded.map((l) => (off.has(l.id) ? { ...l, visible: false } : l)) : withAdded;
  }, [withAdded, rtSources, rtHidden]);

  // Switching theme changes the layers, never the view: the camera the person has is kept (production's
  // cross-theme view continuity). The theme's own camera is the starting view and what Home returns to.
  useEffect(() => {
    if (!mapEngine && !globeEngine) return;
    mapEngine?.setLayers(engineLayers);
    globeEngine?.setLayers(engineLayers);
    performance.mark('mm:theme-applied');
    // Measured from the click in ThemeCarousel; target < 300 ms.
    if (performance.getEntriesByName('mm:theme-click').length > 0) {
      performance.measure('mm:theme-switch', 'mm:theme-click', 'mm:theme-applied');
      performance.clearMarks('mm:theme-click');
    }
  }, [mapEngine, globeEngine, engineLayers]);

  useEffect(() => {
    mapEngine?.setBasemap(basemapId);
    globeEngine?.setBasemap(basemapId);
  }, [mapEngine, globeEngine, basemapId]);

  // The imagery's place names and roads, as the Basemap tab sets them.
  const reference = useLayersPro((s) => s.reference);
  useEffect(() => {
    mapEngine?.setReference?.(reference);
  }, [mapEngine, reference]);

  return (
    <>
      <div
        ref={container}
        className="map"
        role="region"
        aria-label="Map"
        aria-hidden={mode !== 'map'}
        style={{ visibility: mode === 'map' ? 'visible' : 'hidden' }}
      />
      {globeMade && <GlobeView active={mode === 'globe'} onEngine={onGlobe} />}
      {bimMade && (
        <Suspense fallback={null}>
          <BimView active={mode === 'bim'} />
        </Suspense>
      )}
    </>
  );
}
